import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../../environments/environment';

declare const grecaptcha: {
  render: (container: HTMLElement, parameters: { sitekey: string; callback?: () => void; 'expired-callback'?: () => void }) => number;
  getResponse: (widgetId?: number) => string;
  reset: (widgetId?: number) => void;
  ready: (callback: () => void) => void;
};

@Injectable({
  providedIn: 'root'
})
export class CaptchaService {
  private scriptLoading?: Promise<void>;

  constructor(private readonly ngZone: NgZone) {}

  isEnabled(): boolean {
    return environment.captchaEnabled === true && !!environment.recaptchaSiteKey;
  }

  loadScript(): Promise<void> {
    if (!this.isEnabled()) {
      return Promise.resolve();
    }

    if (typeof grecaptcha !== 'undefined') {
      return Promise.resolve();
    }

    if (!this.scriptLoading) {
      this.scriptLoading = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
        document.head.appendChild(script);
      });
    }

    return this.scriptLoading;
  }

  render(container: HTMLElement, onResolved?: () => void, onExpired?: () => void): Promise<number> {
    if (!this.isEnabled()) {
      return Promise.resolve(-1);
    }

    return this.loadScript().then(() => new Promise<number>((resolve) => {
      grecaptcha.ready(() => {
        const widgetId = grecaptcha.render(container, {
          sitekey: environment.recaptchaSiteKey,
          // Google callbacks run outside Angular; re-enter NgZone so UI updates immediately.
          callback: () => this.ngZone.run(() => onResolved?.()),
          'expired-callback': () => this.ngZone.run(() => onExpired?.())
        });
        resolve(widgetId);
      });
    }));
  }

  getResponse(widgetId: number): string {
    if (!this.isEnabled() || widgetId < 0) {
      return '';
    }

    return grecaptcha.getResponse(widgetId) ?? '';
  }

  reset(widgetId: number): void {
    if (!this.isEnabled() || widgetId < 0) {
      return;
    }

    grecaptcha.reset(widgetId);
  }
}
